import type { Decision, IdeaStatus } from './types'

export type IdeaBriefDraft = {
  title: string
  raw_idea: string
  buyer_summary: string
  primary_niche: string
  emotional_driver: string
  recommended_format: string
  ceo_priority: string
}

export type IdeaBriefFields = Pick<
  IdeaBriefDraft,
  'title' | 'raw_idea' | 'buyer_summary' | 'primary_niche' | 'emotional_driver' | 'recommended_format'
>

export type IdeaReadiness = {
  briefComplete: boolean
  researchCount: number
  evaluationCount: number
}

export const validTransitions: Record<IdeaStatus, IdeaStatus[]> = {
  new: ['researching', 'on_hold', 'rejected'],
  researching: ['research_complete', 'on_hold', 'rejected'],
  research_complete: ['council_review', 'researching', 'on_hold', 'rejected'],
  council_review: ['awaiting_ceo', 'researching', 'on_hold', 'rejected'],
  awaiting_ceo: ['ceo_approved', 'revision_requested', 'on_hold', 'rejected'],
  ceo_approved: ['design', 'on_hold'],
  revision_requested: ['researching', 'on_hold', 'rejected'],
  on_hold: ['researching', 'rejected'],
  rejected: ['new', 'retired'],
  design: ['creative_review', 'on_hold'],
  creative_review: ['ready_for_test', 'design', 'on_hold'],
  ready_for_test: ['market_test', 'on_hold'],
  market_test: ['validated', 'revision_requested', 'on_hold', 'rejected'],
  validated: ['scale', 'design', 'on_hold'],
  scale: ['retired'],
  retired: [],
}

export const decisionTargets: Record<Decision['decision'], IdeaStatus> = {
  approve: 'ceo_approved',
  revise: 'revision_requested',
  hold: 'on_hold',
  reject: 'rejected',
}

export function canTransition(from: IdeaStatus, to: IdeaStatus) {
  return validTransitions[from].includes(to)
}

export function isIdeaBriefComplete(brief: IdeaBriefFields) {
  return Object.values(brief).every((value) => Boolean(value?.trim()))
}

export function getDecisionReadinessError(readiness: IdeaReadiness) {
  if (!readiness.briefComplete) return 'Save a complete product brief before opening the decision desk.'
  if (readiness.researchCount < 1) return 'Add at least one research note before opening the decision desk.'
  if (readiness.evaluationCount < 1) return 'Add at least one council readout before opening the decision desk.'
  return null
}

export function getTransitionBlocker(from: IdeaStatus, to: IdeaStatus, readiness: IdeaReadiness) {
  if (!canTransition(from, to)) return 'That stage move is not allowed from the current gate.'

  if (to === 'research_complete' || to === 'council_review') {
    if (!readiness.briefComplete) return 'Save a complete product brief before closing research.'
    if (readiness.researchCount < 1) return 'Add at least one research note before closing research.'
  }

  if (to === 'awaiting_ceo') return getDecisionReadinessError(readiness)
  return null
}

export function validateDecision(status: IdeaStatus, rationale: string, readiness?: IdeaReadiness) {
  if (!rationale.trim()) return 'Write the reason for the call before locking it in.'
  if (status !== 'awaiting_ceo') return 'Move this idea to CEO decision before making the call.'
  if (readiness) return getDecisionReadinessError(readiness)
  return null
}

export function buildIdeaBriefUpdate(draft: IdeaBriefDraft) {
  return {
    title: draft.title.trim(),
    raw_idea: draft.raw_idea.trim(),
    buyer_summary: draft.buyer_summary.trim() || null,
    primary_niche: draft.primary_niche.trim() || null,
    emotional_driver: draft.emotional_driver.trim() || null,
    recommended_format: draft.recommended_format.trim() || null,
    ceo_priority: Number(draft.ceo_priority),
  }
}
